// Request helpers shared by the admin uploaders that send a file in parts
// (sceneLayerUpload.ts, modelMultipartUpload.ts): one retry policy and one way
// of turning a failed response into a readable message.

export const MAX_ATTEMPTS = 3

export class UploadError extends Error {
  readonly status?: number

  constructor(message: string, status?: number) {
    super(message)
    this.status = status
  }
}

export interface UploadRequestOptions {
  /** Attempts in total when retrying (default MAX_ATTEMPTS). */
  attempts?: number
  /** Longest wait between two attempts (default: no cap). */
  maxDelayMs?: number
  /** Bytes of the request body sent so far in the current attempt. Sending
   * then uses XMLHttpRequest, since fetch reports no upload progress. */
  onUploadProgress?: (loaded: number) => void
}

const abortError = () => new DOMException('The upload was cancelled', 'AbortError')

function sleep(ms: number, signal?: AbortSignal | null): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(abortError())
      return
    }
    const onAbort = () => {
      clearTimeout(timer)
      reject(abortError())
    }
    const timer = setTimeout(() => {
      signal?.removeEventListener('abort', onAbort)
      resolve()
    }, ms)
    signal?.addEventListener('abort', onAbort, { once: true })
  })
}

// fetch() semantics (a Response for any HTTP status, a TypeError for a network
// failure, an AbortError when init.signal aborts) plus upload progress.
function sendWithProgress(
  url: string,
  init: RequestInit,
  onUploadProgress: (loaded: number) => void,
): Promise<Response> {
  return new Promise((resolve, reject) => {
    const signal = init.signal
    if (signal?.aborted) {
      reject(abortError())
      return
    }
    const xhr = new XMLHttpRequest()
    const onAbort = () => xhr.abort()
    const settle = () => signal?.removeEventListener('abort', onAbort)
    signal?.addEventListener('abort', onAbort, { once: true })

    xhr.open(init.method || 'GET', url)
    new Headers(init.headers).forEach((value, name) => xhr.setRequestHeader(name, value))
    xhr.responseType = 'blob'
    xhr.upload.onprogress = (event) => onUploadProgress(event.loaded)
    xhr.onload = () => {
      settle()
      if (xhr.status < 200 || xhr.status > 599) {
        reject(new TypeError(`unexpected HTTP status ${xhr.status}`))
        return
      }
      const nullBody = xhr.status === 204 || xhr.status === 205 || xhr.status === 304
      resolve(new Response(nullBody ? null : (xhr.response as Blob), {
        status: xhr.status,
        statusText: xhr.statusText,
      }))
    }
    xhr.onerror = () => {
      settle()
      reject(new TypeError('network error (connection lost, or blocked by the browser)'))
    }
    xhr.onabort = () => {
      settle()
      reject(abortError())
    }
    xhr.send((init.body ?? null) as XMLHttpRequestBodyInit | null)
  })
}

/** Send a request and return its response when it succeeds. With onRetry,
 * network errors, 408, 429 and 5xx are retried with exponential backoff
 * (1 s, 2 s, 4 s, …, up to options.maxDelayMs). An aborted request
 * (init.signal) is never retried and rejects with its AbortError. */
export async function uploadRequest(
  url: string,
  init: RequestInit,
  stage: string,
  onRetry?: (attempt: number) => void,
  options: UploadRequestOptions = {},
): Promise<Response> {
  const attempts = onRetry ? options.attempts ?? MAX_ATTEMPTS : 1
  for (let attempt = 1; ; attempt++) {
    try {
      const response = options.onUploadProgress
        ? await sendWithProgress(url, init, options.onUploadProgress)
        : await fetch(url, init)
      if (!response.ok) {
        let detail = await response.text()
        try {
          const error = JSON.parse(detail)?.error
          if (error) detail = typeof error === 'string' ? error : JSON.stringify(error)
        } catch { /* keep raw */ }
        throw new UploadError(
          `${stage} failed (${response.status}): ${detail || response.statusText}`,
          response.status,
        )
      }
      return response
    } catch (error) {
      if (init.signal?.aborted) throw error
      const status = error instanceof UploadError ? error.status : undefined
      const retryable = status === undefined || status === 408 || status === 429 || status >= 500
      if (!retryable || attempt >= attempts) {
        if (error instanceof UploadError) throw error
        const detail = error instanceof Error ? error.message : String(error)
        throw new UploadError(`${stage} failed: ${detail}. Check your connection and try again.`)
      }
      onRetry?.(attempt + 1)
      await sleep(Math.min(1000 * 2 ** (attempt - 1), options.maxDelayMs ?? Infinity), init.signal)
    }
  }
}

export async function readData<T>(response: Response, stage: string): Promise<T> {
  const result = await response.json().catch(() => null) as { success?: boolean; data?: T } | null
  if (!result?.success || !result.data) {
    throw new Error(`${stage} failed: invalid server response`)
  }
  return result.data
}
